/**
 * ProgramWeekOverview
 *
 * Displays all weeks for a selected program in preview mode.
 * Teachers can browse and navigate to any week without unlock restrictions.
 */

import React, { useEffect, useState } from "react";
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Breadcrumbs,
  Link,
  Chip,
  Skeleton,
  Alert,
  Paper,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";

// Icons
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FolderIcon from "@mui/icons-material/Folder";
import QuizIcon from "@mui/icons-material/Quiz";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

// Services
import {
  previewService,
  PreviewProgramDetail,
  PreviewWeekSummary,
  getProgramDisplayName,
} from "../../../services/previewService";

// Context
import { usePreview } from "../../../context/PreviewContext";

// ============================================================================
// Styled Components
// ============================================================================

const PageHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const ProgramInfoCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  background: `linear-gradient(135deg, ${theme.palette.primary.light}15, ${theme.palette.primary.main}05)`,
  borderLeft: `4px solid ${theme.palette.primary.main}`,
}));

const WeekCard = styled(Card)(({ theme }) => ({
  height: "100%",
  transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[4],
  },
}));

const WeekNumber = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: "50%",
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: "bold",
  fontSize: "1.2rem",
  marginRight: theme.spacing(2),
}));

const StatBadge = styled(Chip)(({ theme }) => ({
  marginRight: theme.spacing(0.5),
  marginTop: theme.spacing(0.5),
}));

// ============================================================================
// Constants
// ============================================================================

const PROGRAM_COLORS: Record<string, string> = {
  yellow: "#FFC107",
  green: "#4CAF50",
  access: "#2196F3",
};

// ============================================================================
// Component
// ============================================================================

const ProgramWeekOverview: React.FC = () => {
  const navigate = useNavigate();
  const { program } = useParams<{ program: string }>();

  // Get cache functions from context
  const { getCachedProgram, setCachedProgram } = usePreview();

  // State
  const [programData, setProgramData] = useState<PreviewProgramDetail | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const programColor =
    PROGRAM_COLORS[program?.toLowerCase() || ""] || "#757575";

  // Fetch program weeks on mount - check cache first
  useEffect(() => {
    const fetchProgramWeeks = async () => {
      if (!program) return;

      // Check cache first
      const cached = getCachedProgram(program);
      if (cached) {
        console.log("[Preview] Using cached program data:", program);
        setProgramData(cached);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await previewService.getProgramWeeks(program);
        setProgramData(data);

        // Cache the response
        setCachedProgram(program, data);
        console.log("[Preview] Cached program data:", program);
      } catch (err: any) {
        console.error("Error fetching program weeks:", err);
        setError(
          err.response?.status === 400
            ? "Invalid program name."
            : "Failed to load program content. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchProgramWeeks();
  }, [program, getCachedProgram, setCachedProgram]);

  // Navigate to week detail
  const handleWeekClick = (weekNumber: number) => {
    navigate(`/teacher/preview/${program}/week/${weekNumber}`);
  };

  // Calculate total questions for a week
  const getTotalQuestions = (week: PreviewWeekSummary): number => {
    return week.modules.reduce((sum, mod) => sum + (mod.questionCount || 0), 0);
  };

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <Container maxWidth="lg">
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 2 }}
      >
        <Link
          component={RouterLink}
          to="/teacher/preview"
          underline="hover"
          color="inherit"
          sx={{ display: "flex", alignItems: "center" }}
        >
          <ArrowBackIcon sx={{ mr: 0.5, fontSize: 18 }} />
          Content Preview
        </Link>
        <Typography color="text.primary" fontWeight="medium">
          {programData?.programName || getProgramDisplayName(program || "")}
        </Typography>
      </Breadcrumbs>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Program Info Header */}
      {loading ? (
        <Skeleton
          variant="rectangular"
          height={120}
          sx={{ mb: 3, borderRadius: 1 }}
        />
      ) : programData ? (
        <ProgramInfoCard elevation={0}>
          <Box display="flex" alignItems="center" gap={2} mb={1}>
            <VisibilityIcon sx={{ color: programColor }} />
            <Typography variant="h5" fontWeight="bold">
              {programData.programName}
            </Typography>
            <Chip label="Preview Mode" size="small" color="info" />
          </Box>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {programData.description}
          </Typography>
          <Box display="flex" gap={2} mt={2}>
            <Chip
              icon={<FolderIcon />}
              label={`${programData.totalWeeks} Weeks`}
              variant="outlined"
            />
            <Chip
              icon={<MenuBookIcon />}
              label={`${programData.modulesPerWeek} Modules per Week`}
              variant="outlined"
            />
            <Chip
              label={programData.cefrLevel}
              variant="outlined"
              color="primary"
            />
          </Box>
        </ProgramInfoCard>
      ) : null}

      {/* Page Header */}
      <PageHeader>
        <Typography variant="h6" color="text.secondary">
          Select a week to preview its content
        </Typography>
      </PageHeader>

      {/* Week Cards Grid */}
      <Grid container spacing={2}>
        {loading ? (
          // Loading skeletons
          <>
            {Array.from({ length: 8 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                <Skeleton
                  variant="rectangular"
                  height={140}
                  sx={{ borderRadius: 1 }}
                />
              </Grid>
            ))}
          </>
        ) : (
          // Week cards
          programData?.weeks.map((week) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={week.weekId}>
              <WeekCard>
                <CardActionArea
                  onClick={() => handleWeekClick(week.weekNumber)}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <WeekNumber sx={{ backgroundColor: programColor }}>
                        {week.weekNumber}
                      </WeekNumber>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {week.weekName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {week.totalModules} modules
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    {/* Module & Question Stats */}
                    <Box mt={1}>
                      <StatBadge
                        icon={<MenuBookIcon sx={{ fontSize: 16 }} />}
                        label={`${week.totalModules} Modules`}
                        size="small"
                        variant="outlined"
                      />
                      <StatBadge
                        icon={<QuizIcon sx={{ fontSize: 16 }} />}
                        label={`${getTotalQuestions(week)} Questions`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </CardContent>
                </CardActionArea>
              </WeekCard>
            </Grid>
          ))
        )}
      </Grid>

      {/* Empty State */}
      {!loading && !error && programData?.weeks.length === 0 && (
        <Box textAlign="center" py={8}>
          <FolderIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No weeks available for this program
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default ProgramWeekOverview;
